import {
  langEnumToLangHeader,
  StorageKeys,
  storageModel,
  LangEnums,
} from "../../libs/storage-manager";

// ============ 通用请求 ============

export type ApiResponse<R = any> = {
  readonly data: { msg: string; code: number; data?: R };
};

async function fetchGet<T = any>(apiPath: string): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(apiPath, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(storageModel[StorageKeys.accessToken]
          ? { Authorization: `Bearer ${storageModel[StorageKeys.accessToken]}` }
          : {}),
        lang: langEnumToLangHeader(
          storageModel[StorageKeys.language] || LangEnums.zhHK,
        ),
        ...(storageModel[StorageKeys.channel]
          ? { channel: storageModel[StorageKeys.channel] }
          : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return { data };
  } catch (err) {
    console.error("fetchGet error:", err);
    return { data: { msg: "網路錯誤", code: 10000 } };
  }
}

// ============ 活动详情接口 ============

/** 奖品项 */
export interface AwardItem {
  /** 层级名称（根据语言返回） */
  levelName: string;
  /** 奖品描述（根据语言返回） */
  awardDesc: string;
  /** 顺序 */
  sequence: number;
  /** 中奖名额 */
  winnerCount: number;
}

/** 签池信息 */
export interface PoolInfo {
  /** 总签数 */
  totalTicketCount: number;
  /** 参与用户数 */
  participantCount: number;
  /** 中签人数 */
  winnerCount: number;
  /** 我的签数 */
  myTicketCount: number;
  /** 我的签池信息 */
  userTicketDetail: UserTicketDetail[];
}

/** 我的签池信息项 */
export interface UserTicketDetail {
  /** 签号 */
  ticketNumber?: string;
  /** 任务 */
  task?: string;
  /** 状态 0-已作废 1-有效 2-已中签 3-未中签 */
  status?: number;
  /** 获奖等级名（仅已中签时有值） */
  levelName?: string;
  /** 创建时间 */
  createTime?: string;
  /** traceId */
  traceId?: string;
}

/** 活动详情 */
export interface ActivityDetail {
  /** 活动编号 */
  activityCode: string;
  /** 活动名称（根据语言返回） */
  activityName: string;
  /** 活动副标题（根据语言返回） */
  activitySubtitle: string;
  /** 活动描述（根据语言返回） */
  activityDesc: string;
  /** 活动规则（根据语言返回） */
  activityRules: string;
  /** banner图url（根据语言返回） */
  bannerUrl: string;
  /** 奖品一览图url（根据语言返回） */
  awardImgUrl: string;
  /** 活动推广页展示名（根据语言返回） */
  activityPromotionShowName: string;
  /** 我的抽签页展示名（根据语言返回） */
  myLotteryShowName: string;
  /** 活动状态 0-未开始 1-进行中 2-待开奖 3-已完成 */
  status: number;
  /** 活动开始时间 */
  activityFrom: string;
  /** 活动结束时间 */
  activityTo: string;
  /** 抽奖日期 */
  lotteryDate: string;
  /** 奖品列表 */
  awards: AwardItem[];
  /** 签池信息 */
  poolInfo: PoolInfo;
}

/**
 * 获取抽奖活动详情
 * GET /chief-operations/api/v1/lottery/activity/detail
 */
export async function getActivityDetail(
  activityCode: string,
): Promise<ApiResponse<ActivityDetail>> {
  return fetchGet<ActivityDetail>(
    `/chiefApi/chief-operations/api/v1/lottery/activity/detail?activityCode=${encodeURIComponent(activityCode)}`,
  );
}

/**
 * 不带 token 的 GET 请求（站外使用）
 */
async function fetchGetPublic<T = any>(
  apiPath: string,
): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(apiPath, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        lang: langEnumToLangHeader(
          storageModel[StorageKeys.language] || LangEnums.zhHK,
        ),
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return { data };
  } catch (err) {
    console.error("fetchGetPublic error:", err);
    return { data: { msg: "網路錯誤", code: 10000 } };
  }
}

/**
 * 查询活动详情【不登录】
 * GET /chief-operations/api/v1/lottery/activity/info
 */
export async function getActivityInfo(
  activityCode: string,
): Promise<ApiResponse<ActivityDetail>> {
  return fetchGetPublic<ActivityDetail>(
    `/chiefApi/chief-operations/api/v1/lottery/activity/info?activityCode=${encodeURIComponent(activityCode)}`,
  );
}

// ============ 用户信息接口 ============

/** 当前登录用户信息 */
export interface CurrentUser {
  userId?: number;
  /** 用户类型（0 普通用户，1 平台用户，2 VIP用户等） */
  userType?: number;
  clientCode?: string;
  loginName?: string;
  nickName?: string;
}

/**
 * 获取当前登录用户信息
 * 测试: api.test.stx365.com/sso-server/api/v3/current/user
 * 生产: api2.chiefgroup.com.hk/sso-server/api/v3/current/user
 */
export async function getCurrentUser(): Promise<ApiResponse<CurrentUser>> {
  return fetchGet<CurrentUser>("/chiefApi/sso-server/api/v3/current/user");
}

/** 客户信息 */
export interface CustomerInfo {
  bizId?: string;
  cid?: string;
  name?: string;
  clientType?: number;
  [key: string]: unknown;
}

/**
 * 获取客户信息
 * GET /customer-info-server/cid/info
 */
export async function getCustomerInfo(): Promise<ApiResponse<CustomerInfo>> {
  return fetchGet<CustomerInfo>("/chiefApi/customer-info-server/cid/info");
}

// ============ 名单校验接口 ============

async function fetchPost<T = any>(
  apiPath: string,
  body: unknown,
): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(apiPath, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(storageModel[StorageKeys.accessToken]
          ? { Authorization: `Bearer ${storageModel[StorageKeys.accessToken]}` }
          : {}),
        lang: langEnumToLangHeader(
          storageModel[StorageKeys.language] || LangEnums.zhHK,
        ),
        ...(storageModel[StorageKeys.channel]
          ? { channel: storageModel[StorageKeys.channel] }
          : {}),
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return { data };
  } catch (err) {
    console.error("fetchPost error:", err);
    return { data: { msg: "網路錯誤", code: 10000 } };
  }
}

/** 名单校验结果项 */
export interface RosterCheckItem {
  activityCode: string;
  inRoster: boolean;
}

/**
 * 检查用户是否在名单中（批量活动编码）
 * POST /chief-operations/api/v1/lottery/activity/roster/check
 */
export async function checkRoster(
  activityCodes: string[],
  rosterType: number = 1,
): Promise<ApiResponse<RosterCheckItem[]>> {
  return fetchPost<RosterCheckItem[]>(
    "/chiefApi/chief-operations/api/v1/lottery/activity/roster/check",
    { activityCodes, rosterType },
  );
}
